import { EditOutlined, CheckCircleOutlined, WarningOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Card, Text, Button, Modal, Form, Input, Select } from '@bancobarigui/barihaus';
import { message, Alert, Tag } from 'antd';
import { useState, useCallback, useRef, useEffect } from 'react';

import { InspectionRequest, InspectionCheckin } from '@/services/types/inspection.types';
import { inspectionService } from '@/services/inspection.service';
import { fetchAddressByCep, isValidCep } from '@/services/geocode.service';
import { getCitiesByState } from '@/services/city.service';
import { BRAZILIAN_STATES } from '@/constants/states';
import { ElementGuard } from '@/components/PermissionGuard';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';

interface InspectionLocationProps {
  inspection: InspectionRequest;
  onUpdate?: () => void;
}

export function InspectionLocation({ inspection, onUpdate }: InspectionLocationProps) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityApiFailed, setCityApiFailed] = useState(false);
  const [lastCepFetched, setLastCepFetched] = useState<string>('');
  const [checkinInfo, setCheckinInfo] = useState<InspectionCheckin | null>(null);
  const [loadingCheckin, setLoadingCheckin] = useState(false);
  const [form] = Form.useForm();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const firstItem = inspection.items?.[0];

  // Buscar informações de checkin se o status for >= CheckinRealizado
  useEffect(() => {
    const fetchCheckinInfo = async () => {
      if (!firstItem?.id) return;

      const status = typeof firstItem.status === 'string'
        ? parseInt(firstItem.status, 10)
        : firstItem.status;

      // Status 8 = CheckinRealizado ou superior
      if (status !== undefined && status >= InspectionItemStatus.CheckinRealizado) {
        setLoadingCheckin(true);
        try {
          const checkin = await inspectionService.getCheckinByItem(firstItem.id);
          setCheckinInfo(checkin);
        } catch {
          setCheckinInfo(null);
        } finally {
          setLoadingCheckin(false);
        }
      }
    };

    fetchCheckinInfo();
  }, [firstItem?.id, firstItem?.status]);

  const stateOptions = BRAZILIAN_STATES.map((state) => ({
    label: `${state.sigla} - ${state.nome}`,
    value: state.sigla,
    disabled: false,
  }));

  const cityOptions = cities.map((city) => ({
    label: city,
    value: city,
    disabled: false,
  }));

  const fetchAddressByCepDebounced = useCallback(
    async (cleanCep: string) => {
      setLastCepFetched(cleanCep);

      try {
        setCepLoading(true);
        const addressData = await fetchAddressByCep(cleanCep);

        if (addressData) {
          form.setFieldsValue({
            street: addressData.street,
            district: addressData.district,
            city: addressData.city,
            state: addressData.state.toUpperCase(),
          });

          if (addressData.state) {
            await loadCitiesForState(addressData.state.toUpperCase());
          }

          setCepError(null);
        } else {
          setCepError('CEP não encontrado. Preencha os campos manualmente.');
        }
      } catch (error) {
        setCepError('Não foi possível buscar o endereço. Preencha manualmente.');
      } finally {
        setCepLoading(false);
      }
    },
    [form]
  );

  const handleCepChange = (e: any) => {
    const value = e?.target?.value || e;

    if (!value || typeof value !== 'string') {
      setCepError(null);
      setLastCepFetched('');
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      return;
    }

    const cleanCep = value.replace(/\D/g, '');

    setCepError(null);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (cleanCep.length === 0 || cleanCep.length < 8) {
      setLastCepFetched('');
    }

    if (cleanCep.length > 0 && cleanCep.length < 8) {
      setCepError('CEP deve ter 8 dígitos');
      return;
    }

    if (cleanCep.length === 8 && isValidCep(cleanCep)) {
      if (cleanCep !== lastCepFetched) {
        debounceTimer.current = setTimeout(() => {
          fetchAddressByCepDebounced(cleanCep);
        }, 800);
      }
    }
  };

  const loadCitiesForState = async (stateCode: string) => {
    if (!stateCode) {
      setCities([]);
      setCityApiFailed(false);
      return;
    }

    setLoadingCities(true);
    try {
      const citiesList = await getCitiesByState(stateCode);
      if (citiesList.length > 0) {
        setCities(citiesList);
        setCityApiFailed(false);
      } else {
        setCities([]);
        setCityApiFailed(true);
      }
    } catch (error) {
      setCities([]);
      setCityApiFailed(true);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleStateChange = async (value: string) => {
    form.setFieldValue('city', undefined);
    await loadCitiesForState(value);
  };

  const handleEditAddress = async () => {
    setCepError(null);
    setCities([]);
    setCityApiFailed(false);
    setLastCepFetched('');
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    form.setFieldsValue({
      cep: firstItem?.cep || '',
      street: firstItem?.street || '',
      number: firstItem?.number || '',
      complement: firstItem?.complement || '',
      district: firstItem?.district || '',
      city: firstItem?.city || '',
      state: firstItem?.state || '',
    });

    if (firstItem?.state) {
      await loadCitiesForState(firstItem.state);
    }

    setIsModalVisible(true);
  };

  const handleSaveAddress = async (values: any) => {
    if (!firstItem?.id) {
      alert('ID do item não encontrado');
      return;
    }

    setLoading(true);

    inspectionService.updateItemAddress(firstItem.id, {
      cep: values.cep,
      street: values.street,
      number: values.number,
      complement: values.complement,
      district: values.district,
      city: values.city,
      state: values.state,
    })
    .then(() => {
      setIsModalVisible(false);
      form.resetFields();
      setCepError(null);
      setCities([]);
      setLastCepFetched('');

      try {
        message.success('Endereço atualizado com sucesso!');
      } catch {
        alert('Endereço atualizado com sucesso!');
      }

      if (onUpdate) {
        setTimeout(() => onUpdate(), 100);
      }
    })
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao atualizar endereço. Tente novamente.';

      try {
        message.error(errorMessage);
      } catch {
        alert(errorMessage);
      }
    })
    .finally(() => {
      setLoading(false);
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setCepError(null);
    setCities([]);
    setCityApiFailed(false);
    setLastCepFetched('');
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  };

  if (!firstItem) {
    return (
      <div className="flex items-center justify-center py-20">
        <Text className="text-gray-500">Nenhum endereço cadastrado para esta inspeção</Text>
      </div>
    );
  }

  const fullAddress = `${firstItem.street}, ${firstItem.number}${firstItem.complement ? ' - ' + firstItem.complement : ''} - ${firstItem.district}, ${firstItem.city}/${firstItem.state}`;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <Card className="shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">CEP</label>
              <p className="mt-1 text-base font-semibold text-gray-900">{firstItem.cep || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Logradouro</label>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {firstItem.street || '-'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Número</label>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {firstItem.number || '-'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Complemento</label>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {firstItem.complement || '-'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Bairro</label>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {firstItem.district || '-'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Município</label>
              <p className="mt-1 text-base font-semibold text-gray-900">{firstItem.city || '-'}</p>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-500">Estado</label>
              <p className="mt-1 text-base font-semibold text-gray-900">{firstItem.state || '-'}</p>
            </div>
          </div>
        </Card>

        <div className="min-h-[300px] xl:min-h-full">
          <div className="w-full h-full bg-gray-100 rounded-lg shadow-md border border-gray-200 flex items-center justify-center overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed`}
              title="Mapa da localização"
            />
          </div>
        </div>
      </div>

      {loadingCheckin && (
        <Card className="shadow-sm">
          <div className="flex items-center justify-center py-4">
            <Text className="text-gray-500">Carregando informações do check-in...</Text>
          </div>
        </Card>
      )}
      {checkinInfo && !loadingCheckin && (
        <Card className="shadow-sm">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <EnvironmentOutlined className="text-blue-600 text-lg" />
              <Text strong className="text-lg">Informações do Check-in</Text>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
            {checkinInfo.distanceFromPropertyMeters !== undefined && checkinInfo.distanceFromPropertyMeters !== null && (
              <span className="text-sm text-gray-700">
                Distância:{' '}
                <strong className={checkinInfo.isWithinAllowedRadius ? 'text-green-600' : 'text-yellow-600'}>
                  {checkinInfo.distanceFromPropertyMeters.toFixed(0)}m
                </strong>
                <span className="text-gray-500"> / {checkinInfo.allowedRadiusMeters || 200}m permitidos</span>
              </span>
            )}
            {checkinInfo.latitude !== undefined && checkinInfo.latitude !== null && checkinInfo.longitude !== undefined && checkinInfo.longitude !== null && (
              <span className="text-sm text-gray-700">
                Coordenadas:{' '}
                <strong className="text-gray-900">
                  Lat: {checkinInfo.latitude.toFixed(6)}, Lng: {checkinInfo.longitude.toFixed(6)}
                </strong>
              </span>
            )}
          </div>

          {!checkinInfo.isWithinAllowedRadius && (
            <Alert
              message="Check-in realizado fora do raio permitido"
              description={
                <div className="mt-2">
                  <p>O inspetor realizou o check-in a uma distância de <strong>{checkinInfo.distanceFromPropertyMeters?.toFixed(0) || '?'}m</strong> do endereço do imóvel.</p>
                  <p className="text-sm text-gray-600 mt-1">Raio permitido: {checkinInfo.allowedRadiusMeters || 200}m</p>
                </div>
              }
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              className="mb-4"
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Data/Hora do Check-in</label>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {new Date(checkinInfo.timestamp).toLocaleString('pt-BR')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Status do Raio</label>
              <div className="mt-1">
                {checkinInfo.isWithinAllowedRadius ? (
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    Dentro do raio permitido
                  </Tag>
                ) : (
                  <Tag icon={<WarningOutlined />} color="warning">
                    Fora do raio permitido
                  </Tag>
                )}
              </div>
            </div>

            {checkinInfo.address && (
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-500">Endereço Registrado</label>
                <p className="mt-1 text-sm text-gray-900">{checkinInfo.address}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {inspection.items && inspection.items.length > 1 && (
        <Card className="shadow-sm">
          <div className="mb-4">
            <Text strong className="text-lg">
              Outros Endereços
            </Text>
            <Text className="text-gray-500 block text-sm">
              Esta inspeção possui {inspection.items.length} endereços cadastrados
            </Text>
          </div>

          <div className="space-y-4">
            {inspection.items.slice(1).map((item, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                <Text strong>Endereço {index + 2}</Text>
                <p className="text-sm text-gray-600 mt-1">
                  {item.street}, {item.number}
                  {item.complement ? ` - ${item.complement}` : ''} - {item.district}, {item.city}/
                  {item.state} - CEP: {item.cep}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex justify-start">
        <ElementGuard permissions={[UserPermissions.inspections_address_edit]}>
          <Button
            buttonColor="primary"
            icon={<EditOutlined />}
            onClick={handleEditAddress}
          >
            Alterar Endereço
          </Button>
        </ElementGuard>
      </div>

      <Modal
        title="Alterar Endereço"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveAddress}
          className="mt-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="CEP"
              name="cep"
              rules={[{ required: true, message: 'CEP é obrigatório' }]}
              help={cepError}
              validateStatus={cepError ? 'error' : ''}
            >
              <Input 
                placeholder="00000-000" 
                onChange={handleCepChange}
                suffix={cepLoading ? <span className="text-blue-500">Buscando...</span> : null}
              />
            </Form.Item>
            
            <Form.Item
              label="Estado"
              name="state"
              rules={[{ required: true, message: 'Estado é obrigatório' }]}
            >
              <Select
                placeholder="Selecione o estado"
                options={stateOptions}
                onChange={handleStateChange}
                showSearch
                filterOption={(input, option) =>
                  (typeof option?.label === 'string' ? option.label : '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>

          <Form.Item
            label="Logradouro"
            name="street"
            rules={[{ required: true, message: 'Logradouro é obrigatório' }]}
          >
            <Input placeholder="Nome da rua/avenida" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Número"
              name="number"
              rules={[{ required: true, message: 'Número é obrigatório' }]}
            >
              <Input placeholder="123" />
            </Form.Item>
            
            <Form.Item
              label="Complemento"
              name="complement"
            >
              <Input placeholder="Apto 101, Bloco A" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Bairro"
              name="district"
              rules={[{ required: true, message: 'Bairro é obrigatório' }]}
            >
              <Input placeholder="Nome do bairro" />
            </Form.Item>
            
            <Form.Item
              label="Cidade"
              name="city"
              rules={[{ required: true, message: 'Cidade é obrigatória' }]}
            >
              {cityApiFailed || (cities.length === 0 && !loadingCities) ? (
                <Input placeholder="Digite o nome da cidade" />
              ) : (
                <Select
                  placeholder="Selecione a cidade"
                  options={cityOptions}
                  loading={loadingCities}
                  showSearch
                  filterOption={(input, option) =>
                    (typeof option?.label === 'string' ? option.label : '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
            </Form.Item>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              buttonColor="primary"
              htmlType="submit"
              loading={loading}
            >
              Salvar Alterações
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
